/**
 * Facility Editor parking inventory — merge + save-plan helpers.
 *
 * Parking lives in two tables:
 *   - shop_bays (bay_category='parking') — FacilityEditor-created parking.
 *     trg_mirror_parking_bay_to_lot_spot copies each row one-way into
 *     parking_lot_spots under the SAME id, so those spot rows are mirrors
 *     and stay owned by shop_bays (a direct spot write would be overwritten
 *     on the next shop_bays save).
 *   - parking_lot_spots with no shop_bays counterpart — "standalone" spots,
 *     e.g. the ones onboarding creates via replace_parking_lot_spots.
 *
 * Standalone spots are edited in place: their id, label and layout are
 * never changed, and they are never inserted into shop_bays (the
 * ensure_bay_has_asset trigger would create a fake Equipment asset per
 * spot). tickets.parking_spot_id has no FK, so an id change would silently
 * orphan ticket references.
 */
import type {
  FacilityEntity,
  ParkingLotSpotRow,
  ShopBayRow,
} from '@/types/facility';
import { ENTITY_RENDER_CONFIG, mapShopBayToFacilityEntity } from '@/types/facility';
import { normalizeParkingLabels } from '@/lib/parkingLabels';

export function isStandaloneParkingSpot(entity: FacilityEntity): boolean {
  return entity.storageSource === 'parking_lot_spot';
}

export function mapParkingLotSpotToFacilityEntity(spot: ParkingLotSpotRow): FacilityEntity {
  const config = ENTITY_RENDER_CONFIG.parking_space;
  return {
    id: spot.id,
    storageSource: 'parking_lot_spot',
    parkingLayoutId: spot.layout_id,
    entityType: 'parking_space',
    label: spot.label,
    x: spot.position_x,
    y: spot.position_y,
    width: spot.width || config.defaultWidth,
    height: spot.height || config.defaultHeight,
    isOutdoor: true,
    isActive: spot.is_active,
    isLockedOut: false,
    lockoutReason: null,
    isCommunity: false,
    assignedTechId: null,
    teamId: null,
    shopId: spot.shop_id,
    bayNumber: null,
    // Not persisted for standalone spots — present only to satisfy the
    // shared render model.
    dbBayType: 'general',
    dbBayCategory: 'parking',
  };
}

/**
 * Build the editor's entity list from both inventories.
 *
 * - Every shop_bays row becomes an entity ('shop_bay'). Only these pass
 *   through normalizeParkingLabels — its renames persist via shop_bays and
 *   the mirror, which does not apply to standalone spots.
 * - A spot whose id equals a shop_bays id is not a separate object (for a
 *   parking bay it is that bay's mirror) and is skipped. Dedupe is by id
 *   only — never by label.
 * - Every other spot becomes a standalone 'parking_lot_spot' entity with
 *   its label untouched.
 *
 * `changed` counts only shop_bay label normalizations, so loading
 * standalone spots never marks the editor dirty.
 */
export function mergeFacilityInventory(
  bayRows: ShopBayRow[],
  spotRows: ParkingLotSpotRow[],
): { entities: FacilityEntity[]; changed: number } {
  const { entities: bayEntities, changed } = normalizeParkingLabels(
    bayRows.map(mapShopBayToFacilityEntity),
  );
  const bayIds = new Set(bayRows.map(b => b.id));
  const standalone = spotRows
    .filter(spot => !bayIds.has(spot.id))
    .map(mapParkingLotSpotToFacilityEntity);
  return { entities: [...bayEntities, ...standalone], changed };
}

/**
 * The only columns this slice writes for a standalone spot. id, layout_id,
 * label, geom, geom_px, image_id, order_index and spot_type are
 * deliberately excluded. Columns are integers, so values are rounded.
 */
export function mapFacilityEntityToParkingSpotGeometryUpdate(entity: FacilityEntity): {
  position_x: number;
  position_y: number;
  width: number;
  height: number;
} {
  return {
    position_x: Math.round(entity.x),
    position_y: Math.round(entity.y),
    width: Math.round(entity.width),
    height: Math.round(entity.height),
  };
}

/**
 * Standalone-spot writes for a Save: one geometry-only UPDATE per spot whose
 * geometry differs from the loaded row, keyed by the spot's existing id.
 * Unchanged spots are skipped so a Save does not touch (and fire realtime
 * for) every spot in the lot.
 */
export function planStandaloneSpotUpdates(
  entities: FacilityEntity[],
  loadedSpots: ParkingLotSpotRow[],
): Array<{ id: string; patch: ReturnType<typeof mapFacilityEntityToParkingSpotGeometryUpdate> }> {
  const loaded = new Map(loadedSpots.map(s => [s.id, s]));
  const plan: Array<{ id: string; patch: ReturnType<typeof mapFacilityEntityToParkingSpotGeometryUpdate> }> = [];
  for (const entity of entities) {
    if (!isStandaloneParkingSpot(entity)) continue;
    const patch = mapFacilityEntityToParkingSpotGeometryUpdate(entity);
    const row = loaded.get(entity.id);
    if (
      row &&
      row.position_x === patch.position_x &&
      row.position_y === patch.position_y &&
      row.width === patch.width &&
      row.height === patch.height
    ) continue;
    plan.push({ id: entity.id, patch });
  }
  return plan;
}

/** Inspector edits a standalone spot may make in this slice. */
const STANDALONE_EDITABLE_KEYS = new Set<keyof FacilityEntity>(['x', 'y', 'width', 'height']);

export function restrictStandaloneSpotUpdates(
  updates: Partial<FacilityEntity>,
): Partial<FacilityEntity> {
  return Object.fromEntries(
    Object.entries(updates).filter(([k]) => STANDALONE_EDITABLE_KEYS.has(k as keyof FacilityEntity)),
  ) as Partial<FacilityEntity>;
}
